import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export function ProtectedRoute({ children, allowedRoles = [] }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading || user === null) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-deep-green border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted text-sm">Chargement…</p>
        </div>
      </div>
    );
  }

  if (user === false) {
    // LOT 525b — La route /login n'existe pas standalone (les routes login
    // publiques sont sous /fr/connexion et /en/login). On redirige vers la
    // version FR par défaut (Daniel/Emilie/Anissa sont francophones).
    // Sans ce fix, Navigate to="/login" tombait dans le wildcard et l'utilisateur
    // se retrouvait sur la homepage /fr au lieu de la page de connexion.
    return <Navigate to="/fr/connexion" state={{ from: location }} replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    if (user.role === 'super_admin' || user.role === 'admin') {
      return <Navigate to="/admin" replace />;
    }
    if (user.role === 'sponsor') {
      return <Navigate to="/sponsor" replace />;
    }
    return <Navigate to="/portal" replace />;
  }

  return children;
}

export function PublicOnlyRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading || user === null) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-deep-green border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted text-sm">Chargement…</p>
        </div>
      </div>
    );
  }

  if (user && user !== false) {
    // Redirect authenticated users to their dashboard
    const from = location.state?.from?.pathname;
    if (from) {
      return <Navigate to={from} replace />;
    }
    if (user.role === 'super_admin' || user.role === 'admin') {
      return <Navigate to="/admin" replace />;
    }
    if (user.role === 'sponsor') {
      return <Navigate to="/sponsor" replace />;
    }
    return <Navigate to="/portal" replace />;
  }

  return children;
}


/**
 * PermissionGuard — wraps a route element and redirects if the admin
 * lacks a specific permission.  Falls back to /admin when access is denied.
 *
 * Usage: <Route path="security" element={<PermissionGuard perm="admin.security"><SecurityConsolePage /></PermissionGuard>} />
 */
export function PermissionGuard({ perm, children }) {
  const { canDo, loading, user } = useAuth();

  if (loading || user === null) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-deep-green border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      </div>
    );
  }

  if (!canDo(perm)) {
    return <Navigate to="/admin" replace />;
  }

  return children;
}
